import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

export class CoreFilterCheckbox implements Plugin {
  private expandButton: HTMLButtonElement | null = null;
  private buttonText: HTMLElement | null = null;
  private buttonCount: HTMLElement | null = null;
  private visibleCount = 5;
  private previousSelections: string[] = [];
  private groupName: string = "";

  private expandLabelText: string = "Ver más";
  private collapseLabelText: string = "Ver menos";

  private observer: MutationObserver | null = null;

  constructor(private readonly element: HTMLElement) {
    // Read config from data attributes
    const dataVisibleCount = this.element.dataset.visibleCount;
    this.visibleCount = dataVisibleCount ? parseInt(dataVisibleCount, 10) : 5;

    const dataGroupName = this.element.dataset.groupName;
    const titleEl = this.element.querySelector(".core-filter-checkbox__title");

    let componentId = this.element
      .querySelector("[data-component-id]")
      ?.getAttribute("data-component-id");
    const checkboxesContainer = this.element.querySelector(
      ".core-filter-checkbox__checkboxes",
    );

    if (!componentId && checkboxesContainer) {
      componentId = `core-filter-checkbox-${Math.random().toString(36).substring(2, 11)}`;
      checkboxesContainer.setAttribute("data-component-id", componentId);
    }

    this.groupName =
      dataGroupName ||
      titleEl?.textContent?.trim() ||
      this.element.id ||
      componentId ||
      `unknown-group-${Math.floor(Math.random() * 1000)}`;

    this.expandLabelText = this.element.dataset.expandLabelText || "Ver más";
    this.collapseLabelText =
      this.element.dataset.collapseLabelText || "Ver menos";

    this.previousSelections = this.getSelectedValues();

    this.expandButton = this.element.querySelector(
      ".core-filter-checkbox__expand-button",
    ) as HTMLButtonElement | null;

    if (!this.expandButton && this.element.hasAttribute("data-visible-count")) {
      this.expandButton = document.createElement("button");
      this.expandButton.type = "button";
      this.expandButton.className = "core-filter-checkbox__expand-button";
      this.expandButton.id = `${componentId}-expand-button`;
      this.expandButton.style.display = "none";
      this.expandButton.setAttribute("aria-expanded", "false");
      this.expandButton.setAttribute(
        "aria-controls",
        `${componentId}-checkboxes`,
      );

      const textSpan = document.createElement("span");
      textSpan.className = "core-filter-checkbox__expand-button-text";
      textSpan.textContent = this.expandLabelText;

      const countSpan = document.createElement("span");
      countSpan.className = "core-filter-checkbox__expand-button-count";

      this.expandButton.appendChild(textSpan);
      this.expandButton.appendChild(countSpan);

      this.element.appendChild(this.expandButton);
    }

    if (this.expandButton) {
      this.buttonText = this.expandButton.querySelector(
        ".core-filter-checkbox__expand-button-text",
      );
      this.buttonCount = this.expandButton.querySelector(
        ".core-filter-checkbox__expand-button-count",
      );
    }
  }

  init(): void {
    this.initExpandableLogic();
    this.initObserver();
    this.bindEvents();
  }

  private bindEvents(): void {
    this.element.addEventListener("change", (e: Event) => {
      const target = e.target as HTMLElement;
      if (
        target &&
        target.tagName === "INPUT" &&
        (target as HTMLInputElement).type === "checkbox"
      ) {
        this.handleChange();
      }
    });

    this.element.addEventListener("core-filter-checkbox:reset", ((
      e: CustomEvent,
    ) => {
      const silent = e.detail?.silent ?? false;
      this.reset({ silent });
    }) as EventListener);

    this.element.addEventListener("core-filter-checkbox:requestState", () => {
      const checkedInputs = Array.from(
        this.element.querySelectorAll("input[type='checkbox']:checked"),
      ) as HTMLInputElement[];

      const checkedValues = checkedInputs.map(
        (cb) =>
          (cb.value && cb.value !== "on" ? cb.value : "") ||
          this.element
            .querySelector(`label[for="${cb.id}"] .core-checkbox__main-label`)
            ?.textContent?.trim() ||
          "",
      );

      this.element.dispatchEvent(
        new CustomEvent("core-filter-checkbox:state", {
          bubbles: true,
          detail: {
            group: this.groupName,
            checked: checkedValues,
          },
        }),
      );
    });

    this.element.addEventListener("core-filter-checkbox:setState", ((
      e: CustomEvent,
    ) => {
      const { checked, silent = false } = e.detail as {
        checked: string[];
        silent?: boolean;
      };
      if (!Array.isArray(checked)) return;

      const checkboxes = this.element.querySelectorAll(
        "input[type='checkbox']",
      ) as NodeListOf<HTMLInputElement>;
      let changed = false;

      checkboxes.forEach((cb) => {
        const labelText =
          (cb.value && cb.value !== "on" ? cb.value : "") ||
          this.element
            .querySelector(`label[for="${cb.id}"] .core-checkbox__main-label`)
            ?.textContent?.trim() ||
          "";

        const shouldBeChecked = checked.includes(labelText);
        if (cb.checked !== shouldBeChecked) {
          cb.checked = shouldBeChecked;
          changed = true;

          const wrapper = cb.closest(".core-checkbox");
          if (wrapper) {
            wrapper.classList.toggle(
              "core-checkbox--selected",
              shouldBeChecked,
            );
          }
        }
      });

      if (changed) {
        if (!silent) {
          this.handleChange();
        } else {
          this.previousSelections = this.getSelectedValues();
        }
      }
    }) as EventListener);
  }

  private handleChange(): void {
    const currentSelections = this.getSelectedValues();

    this.element.dispatchEvent(
      new CustomEvent("core-filter-checkbox:change", {
        bubbles: true,
        detail: {
          group: this.groupName,
          currentSelections: currentSelections,
          previousSelections: this.previousSelections,
        },
      }),
    );

    this.previousSelections = currentSelections;
  }

  private getSelectedValues(): string[] {
    const checkboxes = Array.from(
      this.element.querySelectorAll("input[type='checkbox']"),
    ) as HTMLInputElement[];

    return checkboxes
      .filter((cb) => cb.checked)
      .map((cb) => {
        if (cb.value && cb.value !== "on") return cb.value;
        const label = this.element.querySelector(
          `label[for="${cb.id}"] .core-checkbox__main-label`,
        );
        return label?.textContent?.trim() || "";
      });
  }

  private reset(options?: { silent?: boolean }): void {
    const silent = options?.silent ?? false;
    let changed = false;
    const checkboxes = this.element.querySelectorAll(
      "input[type='checkbox']",
    ) as NodeListOf<HTMLInputElement>;

    checkboxes.forEach((cb) => {
      if (cb.checked) {
        cb.checked = false;
        changed = true;

        const wrapper = cb.closest(".core-checkbox");
        if (wrapper) {
          wrapper.classList.remove("core-checkbox--selected");
        }
      }
    });

    if (changed) {
      if (!silent) {
        this.handleChange();
      } else {
        this.previousSelections = this.getSelectedValues();
      }
    }
  }

  private initExpandableLogic(): void {
    const btnInDom = this.element.querySelector(
      ".core-filter-checkbox__expand-button",
    ) as HTMLButtonElement | null;
    if (btnInDom) {
      this.expandButton = btnInDom;
    }
    if (!this.expandButton) return;

    const checkboxesContainer = this.element.querySelector(
      ".core-filter-checkbox__checkboxes",
    );
    if (!checkboxesContainer) return;

    const checkboxes = Array.from(
      this.element.querySelectorAll("input[type='checkbox']"),
    );

    const totalCheckboxes = checkboxes.length;
    const hiddenCount = Math.max(0, totalCheckboxes - this.visibleCount);

    if (totalCheckboxes <= this.visibleCount) {
      this.expandButton.style.display = "none";
      const allChildren = Array.from(checkboxesContainer.children).filter(
        (child) =>
          !child.hasAttribute("slot") &&
          !child.classList.contains("core-filter-checkbox__expand-button"),
      ) as HTMLElement[];
      allChildren.forEach((el) => {
        el.style.display = "";
        el.style.opacity = "";
      });
      return;
    } else {
      this.expandButton.style.display = "";
    }

    const allChildren = Array.from(checkboxesContainer.children).filter(
      (child) =>
        !child.hasAttribute("slot") &&
        !child.classList.contains("core-filter-checkbox__expand-button"),
    );

    const isExpanded =
      this.expandButton.getAttribute("aria-expanded") === "true";

    const hiddenElements = allChildren.slice(
      this.visibleCount,
    ) as HTMLElement[];

    hiddenElements.forEach((el) => {
      if (!isExpanded) {
        el.style.display = "none";
      } else {
        el.style.display = "";
        el.style.opacity = "1";
      }
    });

    if (this.buttonCount) {
      this.buttonCount.textContent = isExpanded ? "" : `(${hiddenCount})`;
    }

    if (this.buttonText) {
      this.buttonText.textContent = isExpanded
        ? this.collapseLabelText
        : this.expandLabelText;
    }

    const newExpandBtn = this.expandButton.cloneNode(true) as HTMLButtonElement;
    if (this.expandButton.parentNode) {
      this.expandButton.parentNode.replaceChild(
        newExpandBtn,
        this.expandButton,
      );
      this.expandButton = newExpandBtn;
    }

    this.buttonText = this.expandButton.querySelector(
      ".core-filter-checkbox__expand-button-text",
    );
    this.buttonCount = this.expandButton.querySelector(
      ".core-filter-checkbox__expand-button-count",
    );

    this.expandButton.addEventListener("click", () => {
      const currentlyExpanded =
        this.expandButton?.getAttribute("aria-expanded") === "true";

      if (currentlyExpanded) {
        if (this.buttonText) this.buttonText.textContent = this.expandLabelText;
        if (this.buttonCount) this.buttonCount.textContent = `(${hiddenCount})`;
        this.expandButton?.setAttribute("aria-expanded", "false");

        // Esperamos a que termine la transición CSS (TktDispo.scss ~0.55s)
        setTimeout(() => {
          hiddenElements.forEach((el) => {
            el.style.display = "none";
          });
        }, 600);
      } else {
        // Expandir: mostramos primero los elementos ocultos y dejamos
        // que el CSS anime la altura del contenedor.
        hiddenElements.forEach((el) => {
          el.style.display = "";
        });

        if (this.buttonText)
          this.buttonText.textContent = this.collapseLabelText;
        if (this.buttonCount) this.buttonCount.textContent = "";
        this.expandButton?.setAttribute("aria-expanded", "true");
      }
    });
  }

  private initObserver(): void {
    if (this.observer) return;

    this.observer = new MutationObserver((mutations) => {
      let shouldUpdate = false;
      let labelsChanged = false;

      for (const mutation of mutations) {
        if (
          mutation.type === "attributes" &&
          mutation.target === this.element
        ) {
          const attr = mutation.attributeName;
          if (
            attr === "data-expand-label-text" ||
            attr === "data-collapse-label-text"
          ) {
            labelsChanged = true;
          }
          if (attr === "data-visible-count") {
            const newCount = this.element.dataset.visibleCount;
            this.visibleCount = newCount ? parseInt(newCount, 10) : 5;
            shouldUpdate = true;
          }
        }

        if (mutation.type === "childList") {
          if (
            (mutation.target as Element).closest?.(
              ".core-filter-checkbox__checkboxes",
            )
          ) {
            shouldUpdate = true;
            break;
          }

          const hasNewInputs = Array.from(mutation.addedNodes).some(
            (node) =>
              node.nodeType === Node.ELEMENT_NODE &&
              ((node as Element).tagName === "INPUT" ||
                (node as Element).querySelector?.("input[type='checkbox']")),
          );
          const hasRemovedInputs = Array.from(mutation.removedNodes).some(
            (node) =>
              node.nodeType === Node.ELEMENT_NODE &&
              ((node as Element).tagName === "INPUT" ||
                (node as Element).querySelector?.("input[type='checkbox']")),
          );
          if (hasNewInputs || hasRemovedInputs) {
            shouldUpdate = true;
            break;
          }
        }
      }

      if (labelsChanged) {
        this.expandLabelText =
          this.element.dataset.expandLabelText || "Ver más";
        this.collapseLabelText =
          this.element.dataset.collapseLabelText || "Ver menos";
        shouldUpdate = true;
      }

      if (shouldUpdate) {
        this.initExpandableLogic();
      }
    });

    this.observer.observe(this.element, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [
        "data-expand-label-text",
        "data-collapse-label-text",
        "data-visible-count",
      ],
    });
  }

  destroy(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }
}

PluginRegistry.register("core-filter-checkbox", CoreFilterCheckbox);
